import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import DropdownWrapper from './DropdownWrapper';
import TabHeaderNew from './TabHeaderNew';
import TextField from 'components/form-components/TextField';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import FormButton from 'components/form-components/FormButton';
import searchIcon from 'assets/images/icons/search.svg';
import { useTranslation } from 'react-i18next';

const SelectedUnselectedMultiTabSelector = ({
  tabs = [],
  doneAction,
  toggleComponent,
  existingSelections = {},
  onDataLoad,
  lazyLoad = false,
}) => {
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [searchText, setSearchText] = useState('');
  const [dataMap, setDataMap] = useState({});
  const [selectionMap, setSelectionMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const fetchedTabs = useRef({});

  const fetchAllTabs = async () => {
    await Promise.all(
      tabs.map(async (tab) => {
        if (fetchedTabs.current[tab.key]) return;

        // If static data exists → use it
        if (tab.data) {
          setDataMap((prev) => ({
            ...prev,
            [tab.key]: tab.data,
          }));
          fetchedTabs.current[tab.key] = true;
          return;
        }

        // Otherwise fetch
        if (tab.fetch) {
          const res = await tab.fetch();
          if (!res?.status) return;

          const mapped = tab.mapData ? tab.mapData(res) : res.data;

          setDataMap((prev) => ({
            ...prev,
            [tab.key]: mapped,
          }));

          fetchedTabs.current[tab.key] = true;
        }
      }),
    );

    setLoading(false);
  };

  useEffect(() => {
    if (!lazyLoad || isOpen) {
      fetchAllTabs();
    }
  }, [lazyLoad, isOpen]);

  useEffect(() => {
    if (!loading && Object.keys(dataMap).length) {
      const safeCopy = JSON.parse(JSON.stringify(dataMap));
      onDataLoad?.(safeCopy);
    }
  }, [dataMap, loading]);

  useEffect(() => {
    setSelectionMap((prev) => {
      const isSame = JSON.stringify(prev) === JSON.stringify(existingSelections);

      return isSame ? prev : existingSelections;
    });
  }, [existingSelections]);

  // ---------------- Toggle Selection ----------------
  const handleSelect = useCallback((tabKey, id) => {
    setSelectionMap((prev) => {
      const current = new Set(prev[tabKey] || []);
      current.has(id) ? current.delete(id) : current.add(id);

      return {
        ...prev,
        [tabKey]: [...current],
      };
    });
  }, []);

  const clearSelection = () => {
    const cleared = tabs.reduce((acc, tab) => {
      acc[tab.key] = [];
      return acc;
    }, {});

    setSelectionMap(cleared);
  };

  const handleDone = () => {
    doneAction(selectionMap);
  };

  const getFilteredList = () => {
    const list = dataMap[selectedTab.key] || [];
    const selectedIds = selectionMap[selectedTab.key] || [];

    const filtered = list.filter((item) =>
      item.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    return {
      selected: filtered.filter((i) => selectedIds.includes(i.id)),
      unselected: filtered.filter((i) => !selectedIds.includes(i.id)),
    };
  };

  const { selected, unselected } = getFilteredList();

  return (
    <DropdownWrapper
      toggleComponent={toggleComponent}
      onToggle={(open) => {
        setIsOpen(open);

        if (!open) {
          setSearchText('');
          setSelectedTab(tabs[0]);
        }
      }}
      dropdownContent={({ handleCloseDropdown }) => (
        <div className='px-3'>
          <div className='d-flex flex-column gap-3'>
            {/* Tabs */}
            <TabHeaderNew
              tabList={tabs.map((t) => ({
                value: t.key,
                displayValue: t.label,
                icon: t.icon,
              }))}
              selectedTab={{ value: selectedTab.key }}
              setSelectedTab={(tab) => {
                const found = tabs.find((t) => t.key === tab.value);
                setSelectedTab(found);
              }}
              classes={'d-flex theme-font-inter-medium theme-text-black-1'}
            />

            {/* Search */}
            <TextField
              name='search'
              value={searchText}
              handleChange={(_, val) => setSearchText(val)}
              placeholder='Search'
              icon={searchIcon}
              shrink
            />

            <div style={{ maxHeight: 'min(22vh, 24rem)', overflow: 'auto' }}>
              {/* Selected */}
              <List
                title='text_selected'
                list={selected}
                checked
                onChange={(id) => handleSelect(selectedTab.key, id)}
              />

              {/* All */}
              <List
                title='text_all'
                list={unselected}
                checked={false}
                onChange={(id) => handleSelect(selectedTab.key, id)}
              />
            </div>

            {/* Footer */}
            <div className='d-flex justify-content-end gap-3 pt-3 pb-2 border-top-grey-13-h-1'>
              <FormButton text='button_clear' variant='white-1' onClick={clearSelection} minWidth />
              <FormButton
                text='button_done'
                variant='green-1'
                onClick={() => {
                  handleDone();
                  handleCloseDropdown();
                }}
                minWidth
                disabled={loading}
              />
            </div>
          </div>
        </div>
      )}
      zIndex={1055}
      fullWidth
    />
  );
};

SelectedUnselectedMultiTabSelector.propTypes = {
  tabs: PropTypes.array.isRequired,
  doneAction: PropTypes.func.isRequired,
  toggleComponent: PropTypes.node,
  existingSelections: PropTypes.object,
  onDataLoad: PropTypes.func,
  lazyLoad: PropTypes.bool,
};

export default SelectedUnselectedMultiTabSelector;

const List = ({ title, list, checked, onChange }) => {
  const { t } = useTranslation();

  return (
    <div className='mt-2'>
      <div className='fw-bold'>{t(title)}</div>
      <div className='d-flex flex-column gap-2 my-2'>
        {list.map((item) => (
          <SingleCheckBoxField
            key={item.id}
            name={`${title}-${item.id}`}
            value={checked}
            handleChange={() => onChange(item.id)}
            text={<span>{item.name}</span>}
            shrink
          />
        ))}
      </div>
    </div>
  );
};

List.propTypes = {
  title: PropTypes.string.isRequired,
  list: PropTypes.array.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
